import { useEffect, useRef, useState } from "react";

// Exported so a caller that transitions a property of its own — the header fades its
// background in from white — can match the reveal's curve instead of inventing a second one.
export const REVEAL_EASING = "cubic-bezier(0.22, 1, 0.36, 1)";

const EASING = REVEAL_EASING;

// Where the element travels in from. `up` is the default: it rises into place from below.
const START_OFFSETS = {
  up: (distance) => `0, ${distance}px`,
  down: (distance) => `0, -${distance}px`,
  left: (distance) => `-${distance}px, 0`,
  right: (distance) => `${distance}px, 0`,
};

/**
 * Reveals an element the first time it scrolls into view: it fades in and slides in from
 * `direction`, then the observer is disconnected so it never animates again.
 *
 * Returns a ref to put on the element and the sx fragment that drives the transition, so
 * the caller keeps its own layout styles untouched.
 *
 * Pass `onMount` for anything that is on screen from the start and is therefore never
 * scrolled *into* view — the header. Such an element must not be observed at all:
 * IntersectionObserver measures the box **after** its transform, so a reveal that offsets
 * the element clear of the viewport is a deadlock. It starts out not intersecting, the
 * callback never runs, and it stays hidden for good. A 70px header lifted 90px is exactly
 * that case.
 *
 * `threshold` and `rootMargin` are the observer's own options. The defaults wait until 15%
 * of the element is past the bottom eighth of the viewport, which is late for a tall
 * section — pass `threshold: 0, rootMargin: "0px"` to start the reveal the moment its first
 * pixel appears.
 *
 * Two more things to know before using a horizontal direction:
 *
 * - The element overflows its container while it travels, so an ancestor needs
 *   `overflow: hidden` or `overflowX: "clip"`. A slide in from the right without one
 *   flashes a horizontal scrollbar for the length of the animation.
 * - Anything that reserves no height until it loads will fire this at mount while it is
 *   still stacked at the top of the page, and the reveal will be over before the user can
 *   scroll to it. That is why the hero image carries an `aspectRatio`.
 */
const useRevealOnScroll = ({
  delay = 0,
  distance = 48,
  duration = 1400,
  direction = "up",
  onMount = false,
  threshold = 0.15,
  rootMargin = "0px 0px -8% 0px",
} = {}) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Two frames, not one: the hidden state has to be painted before the flip, or the
    // browser coalesces both into a single style change and there is nothing to transition
    // from — the element simply appears.
    if (onMount) {
      let second;
      const first = requestAnimationFrame(() => {
        second = requestAnimationFrame(() => setIsVisible(true));
      });

      return () => {
        cancelAnimationFrame(first);
        cancelAnimationFrame(second);
      };
    }

    const element = ref.current;

    if (!element || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [onMount, threshold, rootMargin]);

  const offset = (START_OFFSETS[direction] || START_OFFSETS.up)(distance);

  const revealSx = {
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? "none" : `translate3d(${offset}, 0)`,
    transition: `opacity ${duration}ms ${EASING} ${delay}ms, transform ${duration}ms ${EASING} ${delay}ms`,
    willChange: "opacity, transform",
    "@media (prefers-reduced-motion: reduce)": {
      opacity: 1,
      transform: "none",
      transition: "none",
    },
  };

  return { ref, isVisible, revealSx };
};

export default useRevealOnScroll;
